import { useQuery } from '@tanstack/react-query';
import { useDomainMutation } from '@pps/mutations';
import { normalizeForSearch } from '@pps/ui/lib/search';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useScopedClubIds } from '@/hooks/useScopedClubIds';
import i18n from '@/i18n';

export interface Teacher {
  id: string;
  club_id: string;
  name: string;
  phone: string | null;
  email: string | null;
  specialty: string | null;
  is_active: boolean;
  avatar_url: string | null;
  created_at: string;
  user_id: string | null;
  /**
   * Fallback SÓ para professor sem login (CHECK no banco:
   * `user_id IS NULL OR (cpf IS NULL AND birth_date IS NULL)`). Com login,
   * CPF/nascimento vivem no escopo do usuário — ler/escrever via
   * usePersonalData (RPCs gated), NUNCA pelo update desta tabela.
   */
  cpf: string | null;
  birth_date: string | null;
}

export const TEACHERS_KEY = ['teachers'] as const;

export interface UseTeachersOptions {
  /**
   * Server-side pagination (IR-RX-007). Quando ausente, hook carrega
   * dataset completo (backwards compat). Quando presente, aplica
   * `.range(from, to)` + `count: 'exact'` e expoe `totalCount`.
   */
  pagination?: { page: number; pageSize: number };
  /**
   * Filtros server-side opcionais. Aplicados antes de `.range()` para que
   * `totalCount` reflita o subset filtrado.
   */
  filters?: {
    /**
     * Search server-side accent-insensitive sobre `club_teachers.name` via
     * `public.immutable_unaccent(name) ILIKE '%term-normalizado%'`. Helper
     * IMMUTABLE + GIN trigram index criados em migration #235. RHS e
     * normalizado client-side com `normalizeForSearch`. Trim vazio = ignorado.
     * Caller responsavel por reset page=1 ao mudar search (IR-RX-008).
     */
    search?: string;
  };
}

export function useTeachers(opts: UseTeachersOptions = {}) {
  const { club, viewScope } = useAuth();
  const { scopedClubIds } = useScopedClubIds();
  const { pagination, filters } = opts;
  const page = pagination?.page ?? 1;
  const pageSize = pagination?.pageSize ?? 25;

  const query = useQuery<{ rows: Teacher[]; totalCount: number }>({
    queryKey: pagination
      ? ['teachers', club?.id, viewScope, filters ?? {}, page, pageSize]
      : ['teachers', club?.id, viewScope, filters ?? {}],
    enabled: !!club?.id && scopedClubIds.length > 0,
    queryFn: async (): Promise<{ rows: Teacher[]; totalCount: number }> => {
      let baseQuery = supabase
        .from('club_teachers')
        .select('*', pagination ? ({ count: 'exact' as const }) : ({} as { count?: 'exact' }))
        .in('club_id', scopedClubIds)
        .order('name');

      // Search server-side accent-insensitive via coluna stored generated
      // `name_unaccent` (migration #285). Ver useStudents.ts para detalhes
      // do padrao (PostgREST nao aceita function-call no LHS de `.filter()`).
      const searchTerm = filters?.search?.trim();
      if (searchTerm) {
        const normalized = normalizeForSearch(searchTerm);
        if (normalized) {
          baseQuery = baseQuery.ilike('name_unaccent', `%${normalized}%`);
        }
      }

      if (pagination) {
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;
        baseQuery = baseQuery.range(from, to);
      }

      const { data, error, count } = await baseQuery;
      if (error) throw error;
      const rows = (data ?? []) as Teacher[];
      return { rows, totalCount: count ?? rows.length };
    },
  });

  const rows = query.data?.rows ?? [];
  const totalCount = query.data?.totalCount ?? 0;

  const create = useDomainMutation({
    mutationFn: async (
      teacher: Omit<Teacher, 'id' | 'created_at' | 'avatar_url' | 'user_id' | 'cpf' | 'birth_date'> &
        Partial<Pick<Teacher, 'cpf' | 'birth_date'>>,
    ) => {
      const { error } = await supabase.from('club_teachers').insert(teacher);
      if (error) throw error;
    },
    invalidates: TEACHERS_KEY,
    toast: { success: i18n.t('toasts.teacherAdded'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'create_teacher' },
  });

  const update = useDomainMutation({
    mutationFn: async ({ id, ...data }: Partial<Teacher> & { id: string }) => {
      // Blindagem RLS (Bug #3 shield pattern — ver useStudents.update).
      // `.select('id').single()` forca PGRST116 quando RLS bloqueia em vez
      // de retorno 200 silencioso.
      const { error } = await supabase
        .from('club_teachers')
        .update(data)
        .eq('id', id)
        .select('id')
        .single();
      if (error) {
        if (error.code === 'PGRST116') {
          throw new Error(i18n.t('teachers.updateBlocked'));
        }
        throw error;
      }
    },
    invalidates: TEACHERS_KEY,
    toast: { success: i18n.t('toasts.teacherUpdated'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'update_teacher' },
  });

  const remove = useDomainMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('club_teachers').update({ is_active: false }).eq('id', id);
      if (error) throw error;
    },
    invalidates: TEACHERS_KEY,
    toast: { success: i18n.t('toasts.teacherDeactivated'), error: i18n.t('toasts.error') },
    tags: { feature: 'crm', action: 'deactivate_teacher' },
  });

  return { ...query, data: rows, teachers: rows, totalCount, create, update, remove };
}
